/**
 * useOpportunityActions Hook
 * 
 * Provides actions for opportunity management:
 * - Delete opportunity
 * - Link/refresh parcel data
 * - Pass on opportunity
 * 
 * Encapsulates API calls per Rule 6.
 */

import { useState, useCallback } from 'react';
import { authApi } from '../shared/utils/api';
import { logger } from '../shared/utils/logger';

interface UseOpportunityActionsProps {
  portfolioId: number;
  opportunityId: number;
  onDeleted?: () => void;
  onUpdated?: () => void;
}

interface ActionResult {
  success: boolean;
  error?: string;
}

interface UseOpportunityActionsReturn {
  // Loading states
  deleting: boolean;
  linkingParcel: boolean;
  passing: boolean;
  
  // Actions - return result directly so callers don't read stale state
  deleteOpportunity: () => Promise<ActionResult>;
  linkParcelData: () => Promise<ActionResult>;
  refreshParcelData: () => Promise<ActionResult>;
  passOpportunity: (reason?: string) => Promise<ActionResult>;
  
  // Error state (still available for render-time display)
  error: string | null;
  clearError: () => void;
}

export function useOpportunityActions({
  portfolioId,
  opportunityId,
  onDeleted,
  onUpdated,
}: UseOpportunityActionsProps): UseOpportunityActionsReturn {
  const [deleting, setDeleting] = useState(false);
  const [linkingParcel, setLinkingParcel] = useState(false);
  const [passing, setPassing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => setError(null), []);

  const deleteOpportunity = useCallback(async (): Promise<ActionResult> => {
    setDeleting(true);
    setError(null);
    
    try {
      logger.info('Deleting opportunity', 'opportunity-actions', { portfolioId, opportunityId });
      const response = await authApi.deleteOpportunity(portfolioId, opportunityId);
      
      if (response.success) {
        logger.info('Opportunity deleted', 'opportunity-actions', { opportunityId });
        onDeleted?.();
        return { success: true };
      } else {
        const msg = response.message || 'Failed to delete opportunity';
        setError(msg);
        return { success: false, error: msg };
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to delete opportunity';
      logger.error('Delete opportunity failed', 'opportunity-actions', msg);
      setError(msg);
      return { success: false, error: msg };
    } finally {
      setDeleting(false);
    }
  }, [portfolioId, opportunityId, onDeleted]);

  const linkParcelData = useCallback(async (): Promise<ActionResult> => {
    setLinkingParcel(true);
    setError(null);
    
    try {
      logger.info('Linking parcel data', 'opportunity-actions', { opportunityId });
      
      const response = await authApi.linkPropertyToParcel(opportunityId, {
        auto_link: true,
        include_neighbors: false,
      });
      
      if (response.success) {
        logger.info('Parcel data linked', 'opportunity-actions', { 
          opportunityId,
          primaryParcel: response.primaryParcel?.parcel_id,
        });
        onUpdated?.();
        return { success: true };
      } else {
        const msg = response.message || 'Failed to link parcel data';
        setError(msg);
        return { success: false, error: msg };
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to link parcel data';
      logger.error('Link parcel failed', 'opportunity-actions', msg);
      setError(msg);
      return { success: false, error: msg };
    } finally {
      setLinkingParcel(false);
    }
  }, [opportunityId, onUpdated]);

  const refreshParcelData = useCallback(async (): Promise<ActionResult> => {
    setLinkingParcel(true);
    setError(null);
    
    try {
      logger.info('Refreshing parcel data', 'opportunity-actions', { opportunityId });
      
      const response = await authApi.refreshPropertyParcels(opportunityId, {
        include_neighbors: false,
      });
      
      if (response.success) {
        logger.info('Parcel data refreshed', 'opportunity-actions', { opportunityId });
        onUpdated?.();
        return { success: true };
      } else {
        const msg = response.message || 'Failed to refresh parcel data';
        setError(msg);
        return { success: false, error: msg };
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to refresh parcel data';
      logger.error('Refresh parcel failed', 'opportunity-actions', msg);
      setError(msg);
      return { success: false, error: msg };
    } finally {
      setLinkingParcel(false);
    }
  }, [opportunityId, onUpdated]);

  const passOpportunity = useCallback(async (reason?: string): Promise<ActionResult> => {
    setPassing(true);
    setError(null);
    
    try {
      logger.info('Passing on opportunity', 'opportunity-actions', { opportunityId, reason });
      
      const response = await authApi.passOpportunity(portfolioId, opportunityId, reason);
      
      if (response.success) {
        logger.info('Opportunity passed', 'opportunity-actions', { opportunityId });
        onUpdated?.();
        return { success: true };
      } else {
        const msg = 'Failed to pass on opportunity';
        setError(msg);
        return { success: false, error: msg };
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to pass on opportunity';
      logger.error('Pass opportunity failed', 'opportunity-actions', msg);
      setError(msg);
      return { success: false, error: msg };
    } finally {
      setPassing(false);
    }
  }, [portfolioId, opportunityId, onUpdated]);

  return {
    deleting,
    linkingParcel,
    passing,
    deleteOpportunity,
    linkParcelData,
    refreshParcelData,
    passOpportunity,
    error,
    clearError,
  };
}

